import { io, type Socket } from "socket.io-client";
import type {
  MessageResponseDto,
  SenderIdentity,
} from "./tanstack-query/dtos/messaging.dto";

export type TypingEventPayload =
  | { conversationId: string; identity: "SELF"; userId: string }
  | { conversationId: string; identity: "ANON" };

export type PresenceEventPayload =
  | {
      conversationId?: string;
      identity: SenderIdentity;
      userId: string;
      online: boolean;
    }
  | {
      conversationId?: string;
      identity: "ANON";
      online: boolean;
    };

export interface MessagingServerEvents {
  "messages:unread": (payload: { count: number }) => void;
  "messages:unread:delta": (payload: { delta: number }) => void;
  "message:new": (payload: MessageResponseDto) => void;
  "message:edited": (payload: MessageResponseDto) => void;
  "message:deleted": (payload: {
    id: string;
    conversationId: string;
    deletedAt: string;
  }) => void;
  "participant:added": (payload: { conversationId: string }) => void;
  "participant:removed": (payload: { conversationId: string }) => void;
  typing: (payload: TypingEventPayload) => void;
  "presence:update": (payload: PresenceEventPayload) => void;
}

export interface MessagingClientEvents {
  subscribe: (
    payload: { conversationId: string },
    ack: (response: { ok: true } | { ok: false; error: string }) => void,
  ) => void;
  typing: (payload: { conversationId: string; isTyping: boolean }) => void;
}

export type MessagingSocket = Socket<
  MessagingServerEvents,
  MessagingClientEvents
>;

export function createMessagingSocket(token: string): MessagingSocket {
  const baseUrl = import.meta.env.VITE_API_URL as string;
  return io(baseUrl, {
    path: "/messaging/socket.io",
    transports: ["websocket"],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
  });
}
